import React, { useContext, useEffect, useState } from 'react';
import { PlusIcon, EllipsisVerticalIcon, EyeIcon, PencilIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Divider } from '../../../components/Divider';
import { Avatar } from '../../../components/Avatar';
import Loading from '../../../components/Loading';
import Pagination from '../../../components/Pagination';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useStockIn } from '../../../hooks/useStockIn';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { StockIn } from '../../../models/StockIn';
import { UserContext } from '../../../context/userContext';
import { Text } from '../../../components/Text';
import { useWarehouses } from '../../../hooks/useWarehouse';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'supplier', label: 'Supplier' },
    { key: 'invoice', label: 'Supplier Invoice No' },
    { key: 'receiver', label: 'Receiver' },
    { key: 'created', label: 'Created on' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const StockInPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { stockIns, loading, error, deleteStockIn, fetchStockIns } = useStockIn();
    const { warehouses } = useWarehouses();

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedStockInId, setSelectedStockInId] = useState<string | null>(null);
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(stockIns) ? stockIns : [])
                .map((s) => new Date(s.created_time).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Stock Ins";
    }, []);

    // Filter StockIn based on multiple criteria
    const filteredStockIn = Array.isArray(stockIns)
        ? stockIns.filter((stockin) => {
            const matchesSearch = stockin.supplier_invoice?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                stockin.reference_no?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesStartDate = !filterStartDate || new Date(stockin.created_time) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(stockin.created_time) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(stockin.created_time).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || stockin.warehouse_id === Number(filterWarehouseId);

            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        })
        : [];

    const handleOpenDeleteDialog = (stockInId: string) => {
        setSelectedStockInId(stockInId);
        setIsOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedStockInId) return;

        setIsDeleting(true);
        try {
            await deleteStockIn(Number(selectedStockInId));
            fetchStockIns();
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete stock in data:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Stock In</Heading>
                </div>
                <Button onClick={() => navigate('/gp/stocks/stockin/add')}><PlusIcon />Create new Stock-in</Button>
            </div>

                <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search stock in…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            {/* Stock In Table */}
            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Reference No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'supplier') && <TableHeader>Supplier</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'invoice') && <TableHeader>Supplier Invoice No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'receiver') && <TableHeader>Receiver</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'created') && <TableHeader>Created on</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredStockIn.length > 0) ? [...filteredStockIn].sort((a, b) => {
                                    const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
                                    return sortOrder === 'newest' ? -diff : diff;
                                }).map((stockIn, index) => (
                                    <TableRow key={stockIn.id} title={`${stockIn.reference_no}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{stockIn.reference_no}</TableCell>
                                        {isColumnVisible(columnVisibility, 'supplier') && <TableCell className="text-zinc-500">{stockIn.supplier_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'invoice') && <TableCell className="text-zinc-500">{stockIn.supplier_invoice}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'receiver') && <TableCell className="text-zinc-500">{stockIn.receiver_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'created') && <TableCell className="text-zinc-500">{dateFormat(stockIn.created_time?.toString(), "mmm dd, yyyy")}</TableCell>}
                                        <TableCell className="py-5 grid grid-cols-3 text-zinc-500">
                                            <a onClick={() => navigate(`/gp/stocks/stockin/edit/${stockIn.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <PencilIcon className='h-5 w-5' />
                                            </a>
                                            <a onClick={() => navigate(`/gp/stocks/stockin/view/${stockIn.id}`)} className="text-zinc-600 hover:text-zinc-900">
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className='h-5 w-5'>
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => handleOpenDeleteDialog(stockIn.id.toString())}>Delete</DropdownItem>
                                                    <DropdownItem onClick={() => navigate(`/gp/stocks/stockin/add/items/${stockIn.id}`)}>Add Stock-in Items</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock in found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id?.toString()}>
                                    {warehouse.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Stock In</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this stock in data? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default StockInPage;
